import type { ChatbotMessagesDto } from "@core/data/dto/chatbot-message.dto";
import type { IotApiClient } from "@core/data/infrastructures/iot-api-client/iot-api-client";
import { ChatbotMapper } from "@core/data/mappers/chatbot.mapper";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { ChatbotMessages } from "@core/domain/models/chatbot-message.model";
import { coreTypes } from "@core/root/core-types.di";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { ok } from "neverthrow";

@injectable()
export class ChatbotDatasource {
    static readonly CHATBOT_PATH = "chatbot";
    static readonly CHATBOT_WEEKLY_REPORT_PATH = "chatbot_weekly_reports";

    constructor(
        @inject(coreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(ChatbotMapper)
        private readonly chatbotMapper: ChatbotMapper,
    ) {}

    getChatbotMessages(): ResultAsync<ChatbotMessages, FallbackError> {
        return this.iotApiClient.http
            .get<ChatbotMessagesDto>(ChatbotDatasource.CHATBOT_PATH)
            .mapErr(() => new FallbackError())
            .map((response) => this.chatbotMapper.mapList(response.data));
    }

    getWeeklyReport(): ResultAsync<ChatbotMessages, FallbackError> {
        return this.iotApiClient.http
            .post<ChatbotMessagesDto>(
                ChatbotDatasource.CHATBOT_WEEKLY_REPORT_PATH,
                {},
            )
            .mapErr(() => new FallbackError())
            .map((response) => this.chatbotMapper.mapList(response.data));
    }

    sendMessage(message: string): ResultAsync<ChatbotMessages, FallbackError> {
        const messageBody = {
            message,
        };

        return this.iotApiClient.http
            .post<ChatbotMessagesDto>(
                ChatbotDatasource.CHATBOT_PATH,
                messageBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                ok(this.chatbotMapper.mapList(response.data)),
            );
    }

    deleteMessages(): ResultAsync<true, FallbackError> {
        return this.iotApiClient.http
            .delete(ChatbotDatasource.CHATBOT_PATH)
            .mapErr(() => new FallbackError())
            .map(() => true);
    }
}
